import React, { useState } from 'react';
import Helmet from 'react-helmet';
import _ from 'lodash';
import moment from 'moment';
import Tippy from '../components/ReactTippy';
import {
  AGENCY_REGISTRATION_APPLICATION_PAGE,
  AGENCY_REGISTRATION_APPLICATION_STATUS,
  HEADER_CONSTANTS,
  ONE_MASTER_FLAG,
} from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';
import { MasterPageHeader } from '../components/PageHeader';
import masterSlideLoader from '../utilities/masterSlideLoader';
import InfinityScrollLoad from '../components/InfinityScrollLoad';
import CompanyService from '../services/Companies';
import AgencyRegistrationApplicationService from '../services/AgencyRegistrationApplications';
import regex from '../utilities/validateRegex';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import { useListSearch } from '../components/useListSearch';
import '../styles/organisms/agent_origins.scss';
import { Confirmation, Information } from '../components/modals/common';
import CommonModal from '../components/modals/common/Modal';
import { StatusTag } from '../components/StatusTag';

const AgentOrigins = () => {
  // 検索項目の表示用の値
  const [companyName, setCompanyName] = useState(null);
  const [address, setAddress] = useState(null);
  const [tel, setTel] = useState(null);
  const [oneMasterFlags, setOneMasterFlags] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [includeRevokeCompany, setIncludeRevokeCompany] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showApprovalHistoryDialog, setShowApprovalHistoryDialog] = useState(
    false
  );
  const [confirmDialog, setConfirmDialog] = useState({
    title: '',
    message: '',
    confirmButtonText: '',
    cancelButtonText: '',
    companyName: '',
  });
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState({
    title: '',
    message: '',
    buttonText: '',
  });

  const {
    list: companies,
    total,
    isLoading,
    listSearch,
    handleScroll,
    searchParamClear,
  } = useListSearch(
    null,
    CompanyService.agentOriginsList,
    response => response.data.entities.companies,
    searchParam => {
      return (
        isNullOrUndefinedOrEmptyStr(searchParam.id) ||
        new RegExp(regex.pattern.integerNoSign).test(searchParam.id)
      );
    }
  );

  // 検索
  const search = () => {
    listSearch({
      name: isNullOrUndefinedOrEmptyStr(companyName) ? null : companyName,
      address: isNullOrUndefinedOrEmptyStr(address) ? null : address,
      tel: isNullOrUndefinedOrEmptyStr(tel) ? null : tel,
      one_master_flags: _.size(oneMasterFlags) > 0 ? oneMasterFlags : null,
      include_rvoke_company_flag: isNullOrUndefinedOrEmptyStr(
        includeRevokeCompany
      )
        ? null
        : includeRevokeCompany,
    });
  };

  const setSearchValue = e => {
    switch (e.target.name) {
      case 'companyName':
        setCompanyName(e.target.value);
        break;
      case 'address':
        setAddress(e.target.value);
        break;
      case 'tel':
        setTel(e.target.value);
        break;
      case 'searchCorporation':
        setOneMasterFlags(
          e.target.checked
            ? [...oneMasterFlags, ONE_MASTER_FLAG.corporation.id]
            : oneMasterFlags.filter(e => e !== ONE_MASTER_FLAG.corporation.id)
        );
        break;
      case 'searchPersonal':
        setOneMasterFlags(
          e.target.checked
            ? [...oneMasterFlags, ONE_MASTER_FLAG.personal.id]
            : oneMasterFlags.filter(e => e !== ONE_MASTER_FLAG.personal.id)
        );
        break;
      case 'includeRevokeCompany':
        setIncludeRevokeCompany(e.target.checked);
        break;
      default:
        break;
    }
  };

  const searchValuesClear = () => {
    setCompanyName(null);
    setAddress(null);
    setTel(null);
    setOneMasterFlags([]);
    setIncludeRevokeCompany(null);
    searchParamClear();
    setShowConfirmDialog(false);
  };

  const handleRevoke = company => {
    const comfirmDialogContent = {
      title: '代行関係を解除',
      message: (
        <span>
          代行関係が解除されると、{company.name}
          のユーザーは自社に対して会社情報や作業員情報の代行操作が出来なくなります。
          <br />
          再度、{company.name}に自社情報の代行操作を依頼する場合には、
          {company.name}のユーザーによる代行登録の再申請が必要です。
          <br />
          <br />
          {company.name}との代行関係を解除してもよろしいですか？
        </span>
      ),
      confirmButtonText: '代行関係解除',
      cancelButtonText: messageUtility.getMessage('I-000-008'),
      onConfirm: () => {
        revokeCompany(company);
      },
    };

    setConfirmDialog(comfirmDialogContent);
    setShowConfirmDialog(true);
  };

  const revokeCompany = company => {
    setShowConfirmDialog(false);
    AgencyRegistrationApplicationService.revokeAgentOrigin({
      agenct_origin_company_id: company.id,
    }).then(response => {
      if (response.result === true) {
        setShowInfoDialog(true);
        setInfoDialog({
          title: '代行関係解除完了',
          message: `${company.name}との代行関係を解除しました。`,
          buttonText: messageUtility.getMessage('I-000-017'),
        });
      } else {
        if (response.status === 400) {
          setShowInfoDialog(true);
          setInfoDialog({
            title: 'エラー',
            message: response.data.message,
            buttonText: messageUtility.getMessage('I-000-017'),
          });
        }
      }
    });
  };

  const handleApprovalHistory = company => {
    setShowApprovalHistoryDialog(true);
    CompanyService.agentOriginsApprovalHistories({
      agent_origin_company_id: company.id,
    }).then(response => {
      if (response.result === true) {
        setApprovalHistory(
          response.data.entities.agency_registration_application
        );
        setShowApprovalHistoryDialog(true);
      }
    });
  };

  const moveDetailPage = id => {
    masterSlideLoader.show(
      AGENCY_REGISTRATION_APPLICATION_PAGE.ORIGIN_DETAIL.COMPONENT_NAME,
      {
        state: { id },
      }
    );
  };

  return (
    <div className='master-slide-view show AgentOrigins'>
      <Helmet>
        <title>
          {`${HEADER_CONSTANTS.conference} - ${
            AGENCY_REGISTRATION_APPLICATION_PAGE.ORIGINS.TEXT
          }`}
        </title>
      </Helmet>
      <div className='master-slide-view__view'>
        <MasterPageHeader
          title={AGENCY_REGISTRATION_APPLICATION_PAGE.ORIGINS.TEXT}
          onClose={() => masterSlideLoader.close()}
        />
        <div id='infinityscroll-target' className='master-slide-view__table'>
          <SearchForm
            {...{
              total,
              companyName,
              address,
              tel,
              setSearchValue,
              searchValuesClear,
              oneMasterFlags,
              includeRevokeCompany,
              search,
            }}
          />
          <div className='master-slide-view__body'>
            <AgencyRegistrationApplicationsTable
              companies={companies}
              moveDetailPage={moveDetailPage}
              handleApprovalHistory={handleApprovalHistory}
              handleRevoke={handleRevoke}
            />
            <InfinityScrollLoad
              targetId={`infinityscroll-target`}
              total={_.size(companies)}
              isLoading={isLoading}
              callback={handleScroll}
              ignoreIndicator={true}
            />
          </div>
        </div>
      </div>
      <AgencyApprovalHistoryTable
        visible={showApprovalHistoryDialog}
        approvalHistory={approvalHistory}
        onClose={() => {
          setShowApprovalHistoryDialog(false);
        }}
        moveDetailPage={moveDetailPage}
      />
      <Confirmation
        visible={showConfirmDialog}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmButtonText={confirmDialog.confirmButtonText}
        cancelButtonText={confirmDialog.cancelButtonText}
        isNegative={true}
        onConfirm={() => confirmDialog.onConfirm()}
        onCancel={() => setShowConfirmDialog(false)}
      />
      <Information
        visible={showInfoDialog}
        onClose={() => {
          setShowInfoDialog(false);
          window.location.reload();
        }}
        title={infoDialog.title}
        message={infoDialog.message}
        buttonText={infoDialog.buttonText}
        multilineText={true}
      />
    </div>
  );
};

const SearchForm = ({
  total,
  companyName,
  address,
  tel,
  setSearchValue,
  searchValuesClear,
  oneMasterFlags,
  includeRevokeCompany,
  search,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const toggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className='master-slide-view__searchform'>
      <div className='search-form'>
        <div className='search-form__header'>
          <div className='search-form__title'>
            検索条件（検索結果<span className='bold'>{total}</span>件)
          </div>
          <button
            id='accordion-toggle'
            className={isOpen ? 'accordion__btn show' : 'accordion__btn'}
            onClick={() => toggle()}
          />
          <button className='btn-text--sm' onClick={searchValuesClear}>
            検索条件クリア
          </button>
        </div>
        <div
          id='search-form-accordion'
          className={isOpen ? 'accordion show' : 'accordion'}
        >
          <div className='search-form__body'>
            <div className='form form--search-form'>
              <div className='form__group flex-wrap'>
                <div className='form-element'>
                  <div className='form-element__label'>
                    <span className='label'>代行元会社名</span>
                  </div>
                  <div className='form-element__element --col-xs-12 --col-md-12 --col-lg-4 --col-xl-4'>
                    <input
                      type='text'
                      name='companyName'
                      placeholder='代行元会社名'
                      value={companyName || ''}
                      onChange={setSearchValue}
                    />
                  </div>
                </div>
                <div className='form-element'>
                  <div className='form-element__label'>
                    <span className='label'>住所</span>
                  </div>
                  <div className='form-element__element --col-xs-12 --col-md-12 --col-lg-4 --col-xl-4'>
                    <input
                      type='text'
                      name='address'
                      placeholder='住所'
                      value={address || ''}
                      onChange={setSearchValue}
                    />
                  </div>
                </div>
                <div className='form-element'>
                  <div className='form-element__label'>
                    <span className='label'>電話番号</span>
                  </div>
                  <div className='form-element__element --col-xs-12 --col-md-12 --col-lg-4 --col-xl-4'>
                    <input
                      type='text'
                      name='tel'
                      placeholder='電話番号'
                      value={tel || ''}
                      onChange={setSearchValue}
                    />
                  </div>
                </div>
                <div className='form-element'>
                  <div className='form-element__label'>
                    <span className='label'>会社種別</span>
                  </div>
                  <div className='form-element__element'>
                    <ul>
                      <li>
                        <label className='checkbox'>
                          <input
                            type='checkbox'
                            name='searchCorporation'
                            disabled={false}
                            checked={oneMasterFlags.includes(
                              ONE_MASTER_FLAG['corporation']['id']
                            )}
                            onChange={setSearchValue}
                            readOnly
                          />
                          <i className='i-square i-3x' />
                          法人
                        </label>
                      </li>
                      <li>
                        <label className='checkbox'>
                          <input
                            type='checkbox'
                            name='searchPersonal'
                            disabled={false}
                            checked={oneMasterFlags.includes(
                              ONE_MASTER_FLAG['personal']['id']
                            )}
                            onChange={setSearchValue}
                            readOnly
                          />
                          <i className='i-square i-3x' />
                          個人事業主
                        </label>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className='form-element'>
                  <div className='element__item'>
                    <div className='AgentOrigins__checkbox'>
                      <label className='checkbox'>
                        <input
                          type='checkbox'
                          name='includeRevokeCompany'
                          disabled={false}
                          checked={includeRevokeCompany}
                          onChange={setSearchValue}
                          readOnly
                        />
                        <i className='i-square i-3x' />
                        解除済の会社を含める
                      </label>
                    </div>
                  </div>
                </div>
                <div className='form-element' style={{ paddingTop: 34 }}>
                  <button
                    className='btn--sm--stay'
                    style={{
                      position: 'relative',
                    }}
                    onClick={search}
                  >
                    検索
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgencyRegistrationApplicationsTable = ({
  companies,
  handleApprovalHistory,
  handleRevoke,
}) => {
  return (
    <div className='constructionlist__list'>
      <table className='table table--constructionlist__list'>
        <thead className='table__header'>
          <tr className='table__label'>
            <td className='value-table value-table--table__label'>
              代行元会社名
            </td>
            <td className='value-table value-table--table__label'>住所</td>
            <td className='value-table value-table--table__label'>電話番号</td>
            <td className='value-table value-table--table__label'>会社種別</td>
            <td className='value-table value-table--table__label'>申請履歴</td>
            <td className='value-table value-table--table__label'>
              代行関係解除
            </td>
          </tr>
        </thead>
        <tbody>
          {_.map(companies, company => (
            <tr
              className={
                company.agency_registration_application.status_no ===
                AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
                  ? 'table__item'
                  : 'table__item AgencyCompanies__table-item--revoked'
              }
              key={company.agency_registration_application.id}
            >
              <td className='value-table'>
                <Tippy
                  duration={10}
                  distance={24}
                  arrowType='sharp'
                  delay={[500, 0]}
                  placement='bottom'
                  followCursor={true}
                  html={
                    <div className='data-table__tooltip'>{company.name}</div>
                  }
                >
                  <div className='value-table__company-name'>
                    {company.name ? company.name : '-'}
                  </div>
                </Tippy>
              </td>
              <td className='value-table'>
                <Tippy
                  duration={10}
                  distance={24}
                  arrowType='sharp'
                  delay={[500, 0]}
                  placement='bottom'
                  followCursor={true}
                  html={
                    <div className='data-table__tooltip'>{company.address}</div>
                  }
                >
                  <div className='value-table__address'>
                    {company.address ? company.company_branch.address : '-'}
                  </div>
                </Tippy>
              </td>
              <td className='value-table'>
                <div className='value-table__tel'>{company.tel}</div>
              </td>
              <td className='value-table'>
                <div className='value-table__text'>
                  {company.one_master_flag == ONE_MASTER_FLAG.personal.id
                    ? '個人事業主'
                    : '法人'}
                </div>
              </td>
              <td className='value-table value-table--button'>
                <div className='AgencyCompanies__table-cell-box'>
                  <button
                    className='btn--sub'
                    onMouseDown={() => handleApprovalHistory(company)}
                  >
                    申請履歴
                  </button>
                </div>
              </td>
              {company.agency_registration_application.status_no ===
              AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID ? (
                <td className='value-table value-table--button'>
                  <div className='AgencyCompanies__table-cell-box'>
                    <button
                      className='btn--negative'
                      onMouseDown={() => handleRevoke(company)}
                    >
                      代行関係を解除
                    </button>
                  </div>
                </td>
              ) : (
                <td className='value-table'>
                  <div className='AgencyCompanies__table-cell-box'>
                    <StatusTag type='suspension'>解除済</StatusTag>
                    <div>
                      <p>
                        解除日：
                        {moment(
                          company.agency_registration_application.revoked_at,
                          'YYYY/MM/DD HH:mm:ss'
                        ).format('YYYY/MM/DD')}
                      </p>
                      <p>
                        解除者：
                        {
                          company.agency_registration_application
                            .revoked_user_name
                        }
                      </p>
                    </div>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AgencyApprovalHistoryTable = props => {
  const { visible, onClose, approvalHistory, moveDetailPage } = props;

  return (
    <React.Fragment>
      {visible && (
        <CommonModal visible={visible}>
          <div className='AgentOrigins'>
            <div className='dialog dialog--modal ApprovalHistoryDialog'>
              <div className='dialog__header'>
                <span className='dialog__title'>申請履歴一覧</span>
              </div>
              <div className='ApprovalHistoryDialog__body'>
                <table className='table'>
                  <thead className='table__header'>
                    <tr className='table__label'>
                      <td className='value-table value-table--table__label'>
                        申請ID
                      </td>
                      <td className='value-table value-table--table__label'>
                        申請作成日
                      </td>
                      <td className='value-table value-table--table__label'>
                        申請更新日
                      </td>
                      <td className='value-table value-table--table__label'>
                        ステータス
                      </td>
                      <td className='value-table value-table--table__label' />
                    </tr>
                  </thead>
                  <tbody className='table__body'>
                    {_.map(approvalHistory, history => (
                      <tr
                        className={
                          history.status_no ===
                          AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
                            ? 'table__item'
                            : 'table__item--revoked'
                        }
                        key={history.id}
                      >
                        <td className='value-table'>{history.id}</td>
                        <td className='value-table'>
                          {moment(
                            history.created_at,
                            'YYYY/MM/DD HH:mm:ss'
                          ).format('YYYY/MM/DD')}
                        </td>
                        <td className='value-table'>
                          {moment(
                            history.updated_at,
                            'YYYY/MM/DD HH:mm:ss'
                          ).format('YYYY/MM/DD')}
                        </td>
                        <td className='value-table'>
                          {history.status_no ===
                          AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
                            ? '承認済み'
                            : '解除済み'}
                        </td>
                        <td className='value-table value-table--button'>
                          <div className='value-table__button-3'>
                            <button
                              className='btn--stay'
                              onMouseDown={() => {
                                moveDetailPage(history.id);
                              }}
                            >
                              確認
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className='dialog__footer'>
                <button
                  className='btn--sub'
                  onClick={onClose ? onClose : undefined}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </CommonModal>
      )}
    </React.Fragment>
  );
};

export default AgentOrigins;



// WEBPACK FOOTER //
// ./src/pages/AgentOrigins.jsx